import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, User, Map, Home, Calendar, Search, X } from 'lucide-react';
import authFetch from '../../utils/authFetch';

// HARDCODED FIX: Smartly switch between Localhost and Secure Server
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const ZKTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // --- SEARCH STATE ---
  const [searchTerm, setSearchTerm] = useState('');
  const [searchColumn, setSearchColumn] = useState('all');

  // --- HELPER: Parse Cadastral String ---
  const parseCadastral = (fullString) => {
    if (!fullString) return { text: '-', id: '-' };
    const match = fullString.match(/\(ID\s+(\d+)\)/i);
    if (match) {
      return { 
        text: fullString.replace(match[0], '').trim(), 
        id: match[1] 
      };
    }
    return { text: fullString, id: '-' };
  };

  // --- HELPER: Split ID Sign into 3 Columns ---
  // Returns { ko, number, part }
  const splitIdSign = (fullSign) => {
    if (!fullSign) return { ko: '-', number: '-', part: '-' };

    // Case 1: Del stavbe (e.g. "del stavbe 1723-4463-41")
    if (fullSign.toLowerCase().includes('del stavbe')) {
      const clean = fullSign.replace(/del stavbe\s+/i, '').trim();
      const parts = clean.split('-');
      if (parts.length >= 3) {
        return { ko: parts[0], number: parts[1], part: parts[2] };
      }
      return { ko: parts[0] || '-', number: parts[1] || '-', part: '-' };
    }

    // Case 2: Parcela (e.g. "parcela 2100 315/2")
    if (fullSign.toLowerCase().includes('parcela')) {
      const clean = fullSign.replace(/^(parcela|zemljiška parcela)\s+/i, '').trim();
      // Usually "KO NUMBER" -> Split by first space
      const spaceIndex = clean.indexOf(' ');
      if (spaceIndex !== -1) {
        return { 
          ko: clean.substring(0, spaceIndex), 
          number: clean.substring(spaceIndex + 1), 
          part: '' // Empty for land
        };
      }
      return { ko: clean, number: '-', part: '' };
    }

    // Fallback
    return { ko: fullSign, number: '-', part: '-' };
  };

  // --- DATA LOADING & PRE-PROCESSING ---
  useEffect(() => {
    authFetch(`${API_BASE}/api/zk`)
      .then(res => res.json())
      .then(fetchedData => {
        
        // 1. Process data ONCE (add parsed fields to object)
        const processed = fetchedData.map(item => {
          const { text, id } = parseCadastral(item.cadastral);
          const { ko, number, part } = splitIdSign(item.id_sign);
          
          return {
            ...item,
            // Derived fields for display & search
            parsed_ko: ko,
            parsed_number: number,
            parsed_part: part,
            
            cadastral_text: text,
            cadastral_id: id,
            category_label: (item.category === 'parcela' || (item.id_sign && item.id_sign.includes('parcela'))) ? 'PARCELA' : 'DEL STAVBE',
          };
        });

        // 2. Sort by Date Extracted (Newest First)
        const sortedData = processed.sort((a, b) => {
          const parseDate = (dateStr) => {
            if (!dateStr) return 0;
            try {
              const [datePart, timePart] = dateStr.split(' - ');
              if (!datePart || !timePart) return 0;
              const [day, month, year] = datePart.split('.');
              return new Date(`${year}-${month}-${day}T${timePart}`);
            } catch (e) { return 0; }
          };
          return parseDate(b.date) - parseDate(a.date);
        });

        setData(sortedData);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  // --- FILTER LOGIC ---
  const filteredData = data.filter(row => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();

    switch (searchColumn) {
      case 'date': 
        return row.date && row.date.toLowerCase().includes(term);
      case 'ko': // New
        return row.parsed_ko.toLowerCase().includes(term);
      case 'number': // New
        return row.parsed_number.toLowerCase().includes(term);
      case 'cadastral_text':
        return row.cadastral_text.toLowerCase().includes(term);
      case 'cadastral_id':
        return row.cadastral_id.toLowerCase().includes(term);
      case 'owner':
        return row.owners && row.owners.some(o => o.imetnik_ime && o.imetnik_ime.toLowerCase().includes(term));
      case 'filename':
        return row.filename.toLowerCase().includes(term);
      case 'type':
        return row.category_label.toLowerCase().includes(term);
      default: // 'all'
        return (
          (row.date && row.date.toLowerCase().includes(term)) || 
          row.parsed_ko.toLowerCase().includes(term) ||
          row.parsed_number.toLowerCase().includes(term) ||
          row.parsed_part.toLowerCase().includes(term) ||
          row.cadastral_text.toLowerCase().includes(term) ||
          row.cadastral_id.toLowerCase().includes(term) ||
          row.filename.toLowerCase().includes(term) ||
          (row.owners && row.owners.some(o => o.imetnik_ime && o.imetnik_ime.toLowerCase().includes(term)))
        );
    }
  });

  const handleClear = () => {
    setSearchTerm('');
    setSearchColumn('all');
  };

  if (loading) return <div style={{ padding: '20px' }}>Loading Land Registry Data...</div>;

  return (
    <div>
      <Link 
        to="/real-estate/raw" 
        style={{ display: 'block', marginBottom: '20px', color: '#64748b', textDecoration: 'none', fontSize: '14px' }}
      >
        ← Back to Menu
      </Link>

      <div style={{display:'flex', justifyContent:'space-between', alignItems:'end', marginBottom:'20px'}}>
        <div>
          <h1 style={{ color: '#1e293b', margin: '0 0 5px 0' }}>Zemljiška Knjiga (Land Registry)</h1>
          <p style={{ color: '#64748b', margin: 0 }}>
            {filteredData.length} extracts found.
          </p>
        </div>
      </div>

      {/* --- SEARCH BAR --- */}
      <div style={searchContainerStyle}>
        <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap'}}>
          
          <div style={{display:'flex', flexDirection:'column'}}>
            <label style={labelStyle}>Search In:</label>
            <select 
              value={searchColumn} 
              onChange={(e) => setSearchColumn(e.target.value)}
              style={selectStyle}
            >
              <option value="all">All Columns</option>
              <option value="date">Date Extracted</option>
              <option value="ko">KO (Cadastral Municipality)</option>
              <option value="number">Parcel / Building Num</option>
              <option value="cadastral_text">Cadastral Name</option>
              <option value="cadastral_id">ID Number</option>
              <option value="owner">Owner Name</option>
              <option value="filename">Filename</option>
              <option value="type">Type</option>
            </select>
          </div>

          <div style={{display:'flex', flexDirection:'column', flexGrow: 1, minWidth: '200px'}}>
            <label style={labelStyle}>Query:</label>
            <div style={{position: 'relative', display: 'flex', alignItems: 'center'}}>
              <Search size={16} color="#94a3b8" style={{position: 'absolute', left: '10px'}} />
              <input 
                type="text" 
                placeholder="Type to search..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={inputStyle}
              />
              {searchTerm && (
                <button onClick={handleClear} style={clearButtonStyle}>
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
          <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <tr>
              <th style={thStyle}>Date Extracted</th>
              <th style={thStyle}>Type</th>
              
              {/* REPLACED ID SIGN COLUMN WITH 3 NEW ONES */}
              <th style={thStyle}>KO</th>
              <th style={thStyle}>Parcela / Stavba</th>
              <th style={thStyle}>Del Stavbe</th>

              <th style={thStyle}>Cadastral Municipality</th>
              <th style={thStyle}>ID Number</th>
              <th style={thStyle}>Owners (Imetniki)</th>
              <th style={thStyle}>Source File</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr><td colSpan="9" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>No results match your search.</td></tr>
            ) : (
              filteredData.map((row, i) => {
                const isParcela = row.category_label === 'PARCELA';
                const badgeColor = isParcela ? '#ecfdf5' : '#eff6ff'; 
                const textColor = isParcela ? '#047857' : '#1d4ed8';
                const Icon = isParcela ? Map : Home;

                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    
                    {/* 1. Date */}
                    <td style={{...tdStyle, whiteSpace: 'nowrap'}}>
                      <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
                        <Calendar size={14} color="#94a3b8"/>
                        {row.date || '-'}
                      </div>
                    </td>

                    {/* 2. Type */}
                    <td style={tdStyle}>
                      <span style={{ 
                        ...badgeStyle, 
                        background: badgeColor, 
                        color: textColor,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Icon size={12} />
                        {row.category_label}
                      </span>
                    </td>

                    {/* 3. KO */}
                    <td style={{...tdStyle, fontWeight: '600', color: '#334155'}}>
                      {row.parsed_ko}
                    </td>

                    {/* 4. Parcel / Building Number */}
                    <td style={{...tdStyle, fontWeight: '700', color: '#1e293b'}}>
                      {row.parsed_number}
                    </td>

                    {/* 5. Part of Building */}
                    <td style={{...tdStyle, color: '#64748b'}}>
                      {row.parsed_part || ''}
                    </td>

                    {/* 6. Cadastral Text (No ID) */}
                    <td style={{...tdStyle, color: '#64748b'}}>
                      {row.cadastral_text}
                    </td>

                    {/* 7. ID Number (Extracted) */}
                    <td style={tdStyle}>
                      <span style={{ 
                        fontFamily: 'monospace', 
                        background: '#f1f5f9', 
                        padding: '2px 6px', 
                        borderRadius: '4px', 
                        color: '#475569',
                        fontWeight: '600'
                      }}>
                        {row.cadastral_id}
                      </span>
                    </td>

                    {/* 8. Owners List */}
                    <td style={tdStyle}>
                      {row.owners && row.owners.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {row.owners.map((owner, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <User size={14} color="#94a3b8" />
                              <span style={{ fontWeight: '500', color: '#334155' }}>
                                {owner.imetnik_ime || 'Unknown'}
                              </span>
                              <span style={{ fontSize: '11px', color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '0 4px', borderRadius: '4px' }}>
                                {owner.delez || '?'}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No owners</span>
                      )}
                    </td>

                    {/* 9. Filename */}
                    <td style={{...tdStyle, fontSize: '11px', color: '#94a3b8', maxWidth: '150px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }} title={row.filename}>
                          <FileText size={14} /> {row.filename}
                      </div>
                    </td>

                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- STYLES ---
const thStyle = { padding: '16px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' };
const tdStyle = { padding: '16px', color: '#334155', verticalAlign: 'top' };
const badgeStyle = { padding: '4px 8px', borderRadius: '6px', fontWeight: '700', fontSize: '11px', letterSpacing: '0.5px' };

const searchContainerStyle = { backgroundColor: '#fff', padding: '15px 20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e2e8f0' };
const labelStyle = { fontSize: '11px', fontWeight: 'bold', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' };
const selectStyle = { padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', backgroundColor: '#f8fafc', cursor: 'pointer', height: '38px' };
const inputStyle = { width: '100%', padding: '8px 12px 8px 35px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box', height: '38px' };
const clearButtonStyle = { position: 'absolute', right: '10px', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center' };

export default ZKTable;